import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
    DndContext,
    DragOverlay,
    defaultDropAnimationSideEffects,
    DragEndEvent,
    DragStartEvent,
    pointerWithin,
    SensorDescriptor,
} from '@dnd-kit/core'
import { SortableContext, horizontalListSortingStrategy, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Stack, Typography } from '@mui/material'
import { useLayoutConstants } from '../hooks/useLayoutConstants'
import type { LayoutConstants } from '../hooks/useLayoutConstants'
import {
    TppCell,
    TppColumn as TppColumnType,
    TppRow,
    TppSection as TppSectionType,
    TppSlot,
    DRAG_TYPES,
} from '../types'
import TppColumn from './TppColumn'
import TppComparisonColumn from './TppComparisonColumn'
import TppSection from './TppSection'
import { EmptyPlaceholder } from '../../../shared/components/EmptyPlaceholder'
import { ComparisonState, ColumnVersionFull } from '../../TPPChangeLog/types'

type TppGridProps = {
    columnIds: string[]
    pinnedColumnIndexById: Record<string, number>
    pinnedColumnIds: string[]
    sectionIds: string[]
    sectionRowIds: Record<string, string[]>
    columns: Record<string, TppColumnType>
    sections: Record<string, TppSectionType>
    rows: Record<string, TppRow>
    slots: Record<string, TppSlot>
    cellLookup: Record<string, Record<string, TppCell>>
    columnsById: Record<string, TppColumnType>
    cellsById: Record<string, TppCell>
    sensors: SensorDescriptor<any>[]
    onDragStart: (event: DragStartEvent) => void
    onDragEnd: (event: DragEndEvent) => void
    onPinToggle: (columnId: string) => Promise<void>
    onLockToggle: (columnId: string) => Promise<void>
    onArchiveToggle: (columnId: string) => Promise<void>
    onSaveVersionClick?: (columnId: string) => void
    isPinning?: boolean
    isLocking?: boolean
    isArchiving?: boolean
    isArchivedTab?: boolean
    onAddSlot: (rowId: string) => void
    isAddingSlot?: boolean
    isSummary: boolean
    visibleColumns: { id: string; label: string }[]
    creatingCellKey: string | null
    onCreateCell: (slotId: string, columnId: string) => Promise<void>
    movingCellId: string | null
    movingSlotId: string | null
    showContext: boolean
    onUpdateSlotContext: (slotId: string, context: string) => Promise<void>
    cornerHeaderContent?: ReactNode
    // Comparison mode
    comparisonState?: ComparisonState | null
    comparisonData?: ColumnVersionFull | null
    onCloseComparison?: () => void
    // Permissions
    canEdit?: boolean
}

const getStickyStyle = (
    colId: string,
    pinnedIndexById: Record<string, number>,
    layout: LayoutConstants
): React.CSSProperties => {
    return {} // @TODO - would be handy for comparison mode
    // if (!(colId in pinnedIndexById)) return {}

    const index = pinnedIndexById[colId]
    const leftOffset = layout.SCROLL_START + index * layout.COLUMN_WIDTH

    return {
        position: 'sticky',
        left: leftOffset,
        zIndex: 25,
        borderRight: '2px solid',
        boxShadow: '4px 0 8px -4px rgba(0,0,0,0.1)',
        borderColor: 'figma.primary',
    }
}

const TppGrid: React.FC<TppGridProps> = ({
    columnIds,
    pinnedColumnIndexById,
    pinnedColumnIds,
    sectionIds,
    sectionRowIds,
    columns,
    sections,
    rows,
    slots,
    cellLookup,
    columnsById,
    cellsById,
    sensors,
    onDragStart,
    onDragEnd,
    onPinToggle,
    onLockToggle,
    onArchiveToggle,
    onSaveVersionClick,
    isPinning,
    isLocking,
    isArchiving,
    isArchivedTab,
    onAddSlot,
    isAddingSlot,
    isSummary,
    visibleColumns,
    creatingCellKey,
    onCreateCell,
    movingCellId,
    movingSlotId,
    showContext,
    onUpdateSlotContext,
    cornerHeaderContent,
    comparisonState,
    comparisonData,
    onCloseComparison,
    canEdit = true,
}) => {
    const layout = useLayoutConstants()

    const isEmpty = sectionIds.length === 0 || columnIds.length === 0
    const [activeDragType, setActiveDragType] = useState<string | null>(null)
    const [activeCellSlotId, setActiveCellSlotId] = useState<string | null>(null)
    const headerColumnsRef = useRef<HTMLDivElement | null>(null)
    const bodyScrollRef = useRef<HTMLDivElement | null>(null)
    const rafRef = useRef<number | null>(null)

    const syncHeaderOffset = useCallback(() => {
        const bodyEl = bodyScrollRef.current
        const headerEl = headerColumnsRef.current
        if (!bodyEl || !headerEl) return
        if (rafRef.current !== null) return
        const { scrollLeft } = bodyEl
        rafRef.current = requestAnimationFrame(() => {
            headerEl.style.transform = `translateX(${-scrollLeft}px)`
            rafRef.current = null
        })
    }, [])

    useEffect(() => {
        syncHeaderOffset()
    }, [syncHeaderOffset, showContext, comparisonState])

    const handleDragStartInternal = (event: DragStartEvent) => {
        const type = event.active.data.current?.type
        setActiveDragType(type || null)
        if (type === DRAG_TYPES.CELL) {
            setActiveCellSlotId(event.active.data.current?.slotId || null)
        } else {
            setActiveCellSlotId(null)
        }
        onDragStart(event)
    }

    const handleDragEndInternal = (event: DragEndEvent) => {
        setActiveDragType(null)
        setActiveCellSlotId(null)
        onDragEnd(event)
    }
    return (
        <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStartInternal}
            onDragEnd={handleDragEndInternal}
        >
            {isEmpty && (
                <Stack direction="column" gap={2}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={2}
                        px={2}
                        py={1.5}
                        bgcolor={'figma.grayscale.contentWrapper'}
                    >
                        {cornerHeaderContent}
                    </Stack>
                    <Stack
                        width="100%"
                        direction="column"
                        gap={1}
                        px={2}
                        py={2.75}
                        bgcolor="figma.grayscale.tier7"
                        borderRadius="16px"
                    >
                        <EmptyPlaceholder title="" subtitle="No data to display" />
                    </Stack>
                </Stack>
            )}
            {!isEmpty && (
                <Stack direction="column" sx={{ width: '100%' }}>
                    <Stack
                        direction="column"
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 40,
                            width: '100%',
                            backgroundColor: 'figma.grayscale.contentWrapper',
                            overflow: 'hidden',
                        }}
                    >
                        <Stack direction="column" minWidth="fit-content">
                            <div
                                style={{
                                    display: 'flex',
                                    position: 'sticky' as const,
                                    top: 0,
                                    zIndex: 40,
                                    width: 'max-content',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={2}
                                    px={2}
                                    bgcolor={'figma.grayscale.contentWrapper'}
                                    sx={{
                                        position: 'sticky' as const,
                                        left: 0,
                                        width: `${layout.SCROLL_START}px`,
                                        minWidth: `${layout.SCROLL_START}px`,
                                        zIndex: 50,
                                    }}
                                >
                                    {cornerHeaderContent}
                                </Stack>

                                {showContext && (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        sx={{
                                            position: 'sticky',
                                            left: layout.SCROLL_START,
                                            zIndex: 35,
                                            flex: '0 0 auto',
                                            width: layout.COLUMN_WIDTH,
                                            padding: '12px 16px',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            bgcolor: 'figma.grayscale.contentWrapper',
                                            borderRight: '1px solid',
                                            borderColor: 'figma.grayscale.stroke',
                                        }}
                                    >
                                        <Typography variant="18x600">Notes</Typography>
                                    </Stack>
                                )}

                                {comparisonState && (
                                    <TppComparisonColumn
                                        title={`${comparisonState.columnTitle}: Version ${comparisonState.versionNumber}`}
                                        onClose={onCloseComparison}
                                        isLoading={!comparisonData}
                                        showContext={showContext}
                                    />
                                )}

                                <Stack
                                    ref={headerColumnsRef}
                                    direction="row"
                                    sx={{
                                        willChange: 'transform',
                                        display: 'flex',
                                    }}
                                >
                                    <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
                                        {columnIds.map((columnId) => (
                                            <TppColumn
                                                key={columnId}
                                                column={columns[columnId]}
                                                isPinned={columnId in pinnedColumnIndexById}
                                                onPinToggle={() => onPinToggle(columnId)}
                                                onLockToggle={() => onLockToggle(columnId)}
                                                onArchiveToggle={() => onArchiveToggle(columnId)}
                                                onSaveVersionClick={
                                                    onSaveVersionClick ? () => onSaveVersionClick(columnId) : undefined
                                                }
                                                isPinning={isPinning}
                                                isLocking={isLocking}
                                                isArchiving={isArchiving}
                                                isArchivedTab={isArchivedTab}
                                                stickyStyle={getStickyStyle(columnId, pinnedColumnIndexById, layout)}
                                                canEdit={canEdit}
                                            />
                                        ))}
                                    </SortableContext>
                                </Stack>
                            </div>
                        </Stack>
                    </Stack>

                    <Stack
                        ref={bodyScrollRef}
                        onScroll={syncHeaderOffset}
                        direction="column"
                        sx={{
                            width: '100%',
                            maxWidth: '100vw',
                            overflowX: 'auto',
                            overflowY: 'visible',
                        }}
                    >
                        <Stack direction="column" minWidth="fit-content">
                            <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
                                {sectionIds.map((secId) => {
                                    const section = sections[secId]
                                    return (
                                        <TppSection
                                            key={secId}
                                            section={{ ...section, rowIds: sectionRowIds[secId] }}
                                            columnIds={columnIds}
                                            rowMap={rows}
                                            slots={slots}
                                            cellLookup={cellLookup}
                                            columnsById={columnsById}
                                            cellsById={cellsById}
                                            pinnedIds={pinnedColumnIds}
                                            onAddSlot={onAddSlot}
                                            isAddingSlot={isAddingSlot}
                                            isSummary={isSummary}
                                            activeDragType={activeDragType}
                                            activeCellSlotId={activeCellSlotId}
                                            visibleColumns={visibleColumns}
                                            creatingCellKey={creatingCellKey}
                                            onCreateCell={onCreateCell}
                                            movingCellId={movingCellId}
                                            movingSlotId={movingSlotId}
                                            showContext={showContext}
                                            onUpdateSlotContext={onUpdateSlotContext}
                                            comparisonData={comparisonData}
                                            isArchivedTab={isArchivedTab}
                                            canEdit={canEdit}
                                        />
                                    )
                                })}
                            </SortableContext>
                        </Stack>
                    </Stack>
                </Stack>
            )}
            <DragOverlay
                dropAnimation={{
                    sideEffects: defaultDropAnimationSideEffects({
                        styles: { active: { opacity: '0.5' } },
                    }),
                }}
            >
                {null}
            </DragOverlay>
        </DndContext>
    )
}

export default TppGrid
